import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, message, Space, Spin, Table, Tag } from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  PrinterOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, formatNumber, toApiNumber } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { formatSettlementNo } from "../../../shared/lib/documentNumbers";
import {
  fetchSystemConfig,
  readCachedSystemConfig,
} from "../../../shared/lib/systemConfig";
import { PageHeader, StatCard, T, cardStyle, Money } from "../../../shared/ui/PageShell";
import { fetchExpenseTypes } from "../lib/expenseTypes";
import { printFarmerSettlementStatement } from "../lib/printFarmerSettlement";
import { vendorFinanceFromConsignment, vendorProductNet } from "../lib/vendorFinance";
import { getPrintedByInfo } from "../../../shared/lib/session";

const FarmerSettlementArchiveView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [archive, setArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      apiClient.get(`/vendor-settlement-archives/${id}`),
      fetchSystemConfig().catch(() => readCachedSystemConfig()),
      fetchExpenseTypes().catch(() => []),
    ])
      .then(([archiveRes, config, types]) => {
        if (!active) return;
        setArchive(archiveRes.data);
        setSystemConfig(config);
        setExpenseTypes(types);
      })
      .catch((error) => {
        if (active) {
          message.error(error?.response?.data?.message || t("loadError", "Failed to load"));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, t]);

  const snapshot = archive?.snapshot || {};
  const mainCode = systemConfig?.currency_code || "IQD";
  const products = useMemo(
    () => (Array.isArray(snapshot.products) ? snapshot.products : []).map((product, index) => ({
      ...product,
      key: product.product_id ?? index,
      displayName: parseLocalizedName(
        product.name || product.product?.name,
        i18n.language,
        product.product_id ? `#${product.product_id}` : "—"
      ),
    })),
    [snapshot.products, i18n.language]
  );
  const expenseLabels = useMemo(
    () => new Map(
      expenseTypes.flatMap((type) => [
        [String(type.code || ""), type.name || type.code],
        [String(type.name || ""), type.name || type.code],
      ])
    ),
    [expenseTypes]
  );
  const expenseRows = useMemo(
    () => (Array.isArray(snapshot.expenses) ? snapshot.expenses : []).map((expense, index) => ({
      ...expense,
      key: expense.id ?? index,
      displayType: expenseLabels.get(String(expense.type_code || expense.type || ""))
        || expense.type_name
        || expense.type
        || "—",
    })),
    [snapshot.expenses, expenseLabels]
  );

  const totalQty = products.reduce(
    (sum, product) => sum + toApiNumber(product.cases_sold, 0),
    0
  );
  const totalWeight = products.reduce(
    (sum, product) => sum + toApiNumber(product.kg_sold, 0),
    0
  );
  const documentNo = archive
    ? archive.document_no || formatSettlementNo(archive.id, archive.settled_at)
    : "";

  const printArchive = async () => {
    if (!archive) return;
    setPrinting(true);
    try {
      await printFarmerSettlementStatement({
        consignment: snapshot,
        vendor: {
          id: archive.vendor_id,
          name: archive.vendor_name || snapshot.vendor?.name,
          phone: archive.vendor_phone || snapshot.vendor?.phone,
        },
        systemConfig,
        language: i18n.language,
        t,
        dateFrom: archive.date_from || snapshot.date_from,
        dateTo: archive.date_to || snapshot.date_to,
        settledAt: archive.settled_at,
        archiveId: archive.id,
        expenseTypes,
        previousDebt: snapshot.previous_debt,
        totalDebt: snapshot.total_debt,
        documentNo,
        note: archive.note || snapshot.note || snapshot.settlement_note || null,
        printedBy: getPrintedByInfo(),
      });
    } catch (error) {
      message.error(error?.message || t("loadError", "Failed to load"));
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 60, textAlign: "center" }}><Spin size="large" /></div>;
  }

  if (!archive) {
    return null;
  }

  // Farmer archive totals = product only (never add snapshot commission back onto net).
  const finance = vendorFinanceFromConsignment({
    ...snapshot,
    total_gross_sold: archive.gross ?? snapshot.total_gross_sold ?? snapshot.total_gross,
    total_gross: archive.gross ?? snapshot.total_gross,
    total_expenses: archive.expenses_total ?? snapshot.total_expenses,
    total_net_owed: archive.net ?? snapshot.total_net_owed,
    total_paid: archive.amount_paid ?? snapshot.total_paid,
    remaining_owed: snapshot.remaining_owed,
    total_commission: 0,
  });
  const gross = finance.gross;
  const expenses = finance.expenses;
  const net = finance.netOwed;

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[
          t("vendorsMenu", "Farmers"),
          t("farmerArchivesTitle", "Settlement Archives"),
          documentNo,
        ]}
        title={documentNo}
        subtitle={
          <span>
            <Link to={`/vendors/${archive.vendor_id}`} style={{ fontWeight: 700 }}>
              {archive.vendor_name || snapshot.vendor?.name || `#${archive.vendor_id}`}
            </Link>
            {" · "}
            {(archive.date_from || "—") + " → " + (archive.date_to || "—")}
          </span>
        }
        action={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/farmer-settlement-archives")}>
              {t("back", "Back")}
            </Button>
            <Button type="primary" icon={<PrinterOutlined />} loading={printing} onClick={printArchive}>
              {t("print", "Print")}
            </Button>
          </Space>
        }
      />

      <div className="velan-stats-row velan-stats-row--dense">
        <StatCard icon={<ShopOutlined style={{ color: "#2563eb" }} />} label={t("vendorNetQtySold", "Qty sold (net)")} value={formatNumber(totalQty)} iconBg="#dbeafe" />
        <StatCard icon={<ShopOutlined style={{ color: "#0d9488" }} />} label={t("vendorNetWeightSold", "Weight sold (net)")} value={`${formatNumber(totalWeight)} ${t("kg", "kg")}`} iconBg="#ccfbf1" />
        <StatCard icon={<DollarOutlined style={{ color: "#0891b2" }} />} label={t("vendorGross", "Gross")} value={formatCurrency(gross, mainCode)} iconBg="#cffafe" />
        <StatCard icon={<DollarOutlined style={{ color: "#d97706" }} />} label={t("batchExpenses", "Expenses")} value={formatCurrency(expenses, mainCode)} iconBg="#fef3c7" />
        <StatCard icon={<DollarOutlined style={{ color: "#16a34a" }} />} label={t("vendorNet", "Net")} value={formatCurrency(net, mainCode)} iconBg="#dcfce7" valueColor={T.green} />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, fontWeight: 700 }}>
          {t("vendorPerItemTitle", "Settlement per item")}
        </div>
        <Table
          size="small"
          pagination={false}
          dataSource={products}
          locale={{ emptyText: t("noDataFound", "No data found") }}
          scroll={{ x: 980 }}
          columns={[
            {
              title: <b>{t("product", "Product")}</b>,
              dataIndex: "displayName",
              key: "product",
              width: 180,
            },
            {
              title: <b>{t("qty", "Qty")}</b>,
              dataIndex: "cases_sold",
              key: "qty",
              align: "right",
              width: 90,
              render: (value) => <Money>{formatNumber(value)}</Money>,
            },
            {
              title: <b>{t("weight", "Weight")}</b>,
              dataIndex: "kg_sold",
              key: "weight",
              align: "right",
              width: 110,
              render: (value) => <Money>{`${formatNumber(value)} ${t("kg", "kg")}`}</Money>,
            },
            {
              title: <b>{t("vendorGross", "Gross")}</b>,
              key: "gross",
              align: "right",
              width: 140,
              render: (_, row) => <Money>{formatCurrency(row.gross ?? row.gross_sold, mainCode)}</Money>,
            },
            {
              title: <b>{t("batchExpenses", "Expenses")}</b>,
              dataIndex: "expenses",
              key: "expenses",
              align: "right",
              width: 140,
              render: (value) => <Money>{formatCurrency(value, mainCode)}</Money>,
            },
            {
              title: <b>{t("vendorNet", "Net")}</b>,
              key: "net",
              align: "right",
              width: 140,
              render: (_, row) => (
                <Money strong color="#0891b2">{formatCurrency(vendorProductNet(row), mainCode)}</Money>
              ),
            },
          ]}
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, fontWeight: 700 }}>
          <DollarOutlined style={{ marginInlineEnd: 8, color: "#d97706" }} />
          {t("batchExpenses", "Expenses")}
        </div>
        <Table
          size="small"
          pagination={false}
          dataSource={expenseRows}
          locale={{ emptyText: t("vendorNoExpenses", "No expenses yet") }}
          scroll={{ x: 680 }}
          columns={[
            {
              title: <b>{t("type", "Type")}</b>,
              dataIndex: "displayType",
              key: "type",
              render: (value) => <Tag color="orange">{value}</Tag>,
            },
            {
              title: <b>{t("product", "Product")}</b>,
              key: "product",
              render: (_, row) => row.product_id
                ? (
                  <span style={{ fontWeight: 600 }}>
                    {parseLocalizedName(row.product_name, i18n.language, `#${row.product_id}`)}
                  </span>
                )
                : <span style={{ color: T.muted }}>{t("vendorExpenseFullAccount", "Full account")}</span>,
            },
            {
              title: <b>{t("amountPaid", "Amount")}</b>,
              dataIndex: "amount",
              key: "amount",
              align: "right",
              render: (value) => <Money strong color="#d97706">−{formatCurrency(value, mainCode)}</Money>,
            },
            {
              title: <b>{t("note", "Note")}</b>,
              dataIndex: "note",
              key: "note",
              render: (value) => <span style={{ color: T.sub }}>{value || "—"}</span>,
            },
            {
              title: <b>{t("date", "Date")}</b>,
              dataIndex: "created_at",
              key: "date",
              width: 120,
              render: (value) => (
                <span style={{ color: T.muted, fontSize: 12 }}>
                  {value ? dayjs(value).format("DD/MM/YYYY") : "—"}
                </span>
              ),
            },
          ]}
        />
      </div>

      <div style={{ ...cardStyle, padding: "14px 20px", color: T.sub, fontSize: 13 }}>
        {t("date", "Date")}:{" "}
        <b>{archive.settled_at ? dayjs(archive.settled_at).format("DD/MM/YYYY HH:mm") : "—"}</b>
        {" · "}
        {t("clearedBy", "Cleared by")}:{" "}
        <b style={{ color: T.text }}>{archive.created_by_name || "—"}</b>
        {" · "}
        {t("vendorAlreadyPaid", "Paid")}:{" "}
        <Money strong>{formatCurrency(archive.amount_paid, mainCode)}</Money>
      </div>
    </div>
  );
};

export default FarmerSettlementArchiveView;
